import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { COMPANY, buildWhatsAppUrl, callUrl, mailUrl } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.message) {
      toast.error("Please fill name, mobile and message");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSent(true);
      toast.success("Message sent. We'll respond shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send. Please try WhatsApp or call.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="py-16 lg:py-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-semibold tracking-[0.18em] uppercase text-[#081537] mb-3">Contact Us</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] text-balance max-w-3xl">
            Talk to a moving expert today.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Call, WhatsApp, email or send us a message. We respond within 2 hours, 7 days a week.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div className="space-y-5">
            <a href={callUrl} data-testid="contact-call-card" className="block bg-white border border-slate-200 hover:border-[#081537] rounded-2xl p-6 transition-all hover:shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#081537] grid place-items-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Call</div>
                  <div className="font-semibold text-[#0F172A] text-lg">{COMPANY.phone}</div>
                </div>
              </div>
            </a>
            <a href={buildWhatsAppUrl()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-card" className="block bg-white border border-slate-200 hover:border-green-500 rounded-2xl p-6 transition-all hover:shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 grid place-items-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">WhatsApp</div>
                  <div className="font-semibold text-[#0F172A] text-lg">{COMPANY.phone}</div>
                </div>
              </div>
            </a>
            <a href={mailUrl} data-testid="contact-email-card" className="block bg-white border border-slate-200 hover:border-[#081537] rounded-2xl p-6 transition-all hover:shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#081537] grid place-items-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Email</div>
                  <div className="font-semibold text-[#0F172A] text-lg">{COMPANY.email}</div>
                </div>
              </div>
            </a>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 grid place-items-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Office</div>
                  <div className="font-semibold text-[#0F172A]">{COMPANY.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            {sent ? (
              <div data-testid="contact-success" className="rounded-2xl border border-green-200 bg-green-50 p-10 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500 text-white grid place-items-center mb-5">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h3 className="font-[Outfit] text-2xl font-bold text-[#0F172A]">Thanks! We&apos;ll be in touch.</h3>
                <p className="mt-3 text-slate-600">Our team will respond shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xl shadow-blue-900/5">
                <h3 className="font-[Outfit] text-2xl font-semibold text-[#0F172A] mb-1">Send us a message</h3>
                <p className="text-sm text-slate-500 mb-6">We respond within 2 hours.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</Label>
                    <Input data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} />
                  </div>
                  <div>
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile *</Label>
                    <Input data-testid="contact-mobile" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} inputMode="tel" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">Email</Label>
                    <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</Label>
                    <Input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</Label>
                    <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} />
                  </div>
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="mt-6 w-full bg-[#081537] hover:bg-[#0F2152] text-white rounded-full h-12 font-semibold">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
